import { useEffect, useState, useSyncExternalStore } from "react";
import { scopedAccountId, withAccountScope, ALL_ACCOUNTS } from "@/lib/accountScope";

const initialState = (scope) => ({
  items: [], total: 0, page: 1, pages: 1, scope,
  categories: { income: [], expense: [] },
  categoriesReady: false, loading: true,
  selectedIds: new Set(), selectionMode: false, importingFile: "", deletingBulk: false,
  filters: { search: "", type: "all", category: "all", startDate: "", endDate: "" },
  error: null, categoryError: null,
});

export class LedgerInteraction {
  constructor(backend, { debounceMs = 250, scope = ALL_ACCOUNTS } = {}) {
    this.backend = backend;
    this.debounceMs = debounceMs;
    this.state = initialState(scope);
    this.listeners = new Set();
    this.queue = Promise.resolve();
    this.timer = null;
    this.destroyed = false;
  }

  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => this.state;

  setState(change) {
    this.state = { ...this.state, ...change };
    this.listeners.forEach((listener) => listener());
  }

  enqueue(request) {
    const next = this.queue.catch(() => {}).then(request);
    this.queue = next;
    return next;
  }

  query() {
    const { filters, page } = this.state;
    const query = new URLSearchParams();
    if (filters.type !== "all") query.set("type", filters.type);
    if (filters.category !== "all") query.set("category", filters.category);
    if (filters.search) query.set("search", filters.search);
    if (filters.startDate) query.set("start_date", filters.startDate);
    if (filters.endDate) query.set("end_date", filters.endDate);
    query.set("page", String(page));
    query.set("page_size", "50");
    return withAccountScope(query, this.state.scope);
  }

  /** The global account filter; imports and new rows follow it too. */
  get accountId() {
    return scopedAccountId(this.state.scope);
  }

  async start() {
    // Re-startable on purpose. React re-runs an effect after its cleanup —
    // StrictMode does it on every mount in development — so an instance that
    // stayed destroyed after the first cleanup would drop every later
    // response on the floor and leave the page on its loading skeleton.
    this.destroyed = false;
    try {
      const categories = await this.enqueue(() => this.backend.listCategories());
      if (this.destroyed) return;
      this.setState({ categories, categoriesReady: true, categoryError: null });
    } catch (error) {
      if (!this.destroyed) this.setState({ categoriesReady: true, categoryError: error });
    }
    try { await this.reload(); } catch (_) {}
  }

  async reload() {
    this.setState({ loading: true });
    try {
      const data = await this.enqueue(() => this.backend.listTransactions(this.query()));
      if (!this.destroyed) {
        this.setState({
          items: data.items, total: data.total, page: data.page,
          pages: data.pages, loading: false, error: null,
        });
      }
      return data;
    } catch (error) {
      if (!this.destroyed) this.setState({ loading: false, error });
      throw error;
    }
  }

  scheduleReload() {
    clearTimeout(this.timer);
    if (!this.debounceMs) return this.reload();
    this.timer = setTimeout(() => this.reload().catch(() => {}), this.debounceMs);
    return Promise.resolve();
  }

  setFilter(name, value) {
    this.setState({
      filters: { ...this.state.filters, [name]: value },
      page: 1,
      selectedIds: new Set(),
    });
    return this.scheduleReload();
  }

  setScope(scope) {
    if (scope === this.state.scope) return Promise.resolve();
    this.setState({ scope, page: 1, selectedIds: new Set() });
    return this.scheduleReload();
  }

  setPage(page) {
    this.setState({ page });
    return this.scheduleReload();
  }

  toggleSelected(id, checked) {
    const selectedIds = new Set(this.state.selectedIds);
    if (checked) selectedIds.add(id);
    else selectedIds.delete(id);
    this.setState({ selectedIds });
  }

  /**
   * Selection mode exists for touch, where there is no room for a permanent
   * checkbox column: a long press turns the list into a picker. It is separate
   * state rather than `selectedIds.size > 0` because emptying the selection by
   * hand should leave the user in the picker, not kick them out of it.
   */
  enterSelectionMode(id) {
    const selectedIds = new Set(this.state.selectedIds);
    if (id) selectedIds.add(id);
    this.setState({ selectionMode: true, selectedIds });
  }

  togglePage(checked) {
    const selectedIds = new Set(this.state.selectedIds);
    this.state.items.forEach((item) => {
      if (checked) selectedIds.add(item.id);
      else selectedIds.delete(item.id);
    });
    this.setState({ selectedIds });
  }

  clearSelection() {
    this.setState({ selectedIds: new Set(), selectionMode: false });
  }

  clearError() {
    this.setState({ error: null });
  }

  clearCategoryError() {
    this.setState({ categoryError: null });
  }

  async deleteOne(id) {
    const result = await this.enqueue(() => this.backend.deleteTransaction(id));
    const selectedIds = new Set(this.state.selectedIds);
    selectedIds.delete(id);
    this.setState({ selectedIds });
    this.reload().catch(() => {});
    return result;
  }

  async deleteSelected() {
    const ids = [...this.state.selectedIds];
    if (!ids.length) return { deleted: 0 };
    this.setState({ deletingBulk: true });
    try {
      const result = await this.enqueue(() => this.backend.bulkDelete(ids));
      this.setState({ selectedIds: new Set(), selectionMode: false, page: 1, deletingBulk: false });
      this.reload().catch(() => {});
      return result;
    } catch (error) {
      this.setState({ deletingBulk: false });
      throw error;
    }
  }

  async importCsv(file) {
    this.setState({ importingFile: file.name });
    try {
      const result = await this.enqueue(() => this.backend.importCsv(file, this.accountId));
      this.setState({ page: 1 });
      this.reload().catch(() => {});
      return result;
    } finally {
      this.setState({ importingFile: "" });
    }
  }

  async mutate(request) {
    const result = await this.enqueue(request);
    this.reload().catch(() => {});
    return result;
  }

  saveTransaction(editing, payload) {
    return this.mutate(() => this.backend.saveTransaction(editing, payload));
  }

  saveTransfer(transferId, payload) {
    return this.mutate(() => this.backend.saveTransfer(transferId, payload));
  }

  linkTransfer(transactionId, otherTransactionId) {
    return this.mutate(() => this.backend.linkTransfer(transactionId, otherTransactionId));
  }

  unlinkTransfer(transferId) {
    return this.mutate(() => this.backend.unlinkTransfer(transferId));
  }

  transferCandidates(transactionId) {
    return this.enqueue(() => this.backend.transferCandidates(transactionId));
  }

  previewGooglePay(file) {
    return this.enqueue(() => this.backend.previewGooglePay(file));
  }

  commitGooglePay(rows) {
    return this.mutate(() => this.backend.commitGooglePay(rows, this.accountId));
  }

  destroy() {
    this.destroyed = true;
    clearTimeout(this.timer);
    this.listeners.clear();
  }
}

export function createHttpLedgerAdapter(api) {
  return {
    async listCategories() {
      const response = await api.get("/categories");
      return response.data;
    },
    async listTransactions(query) {
      const response = await api.get(`/transactions?${query.toString()}`);
      return Array.isArray(response.data)
        ? { items: response.data, total: response.data.length, page: 1, pages: 1 }
        : response.data;
    },
    deleteTransaction: (id) => api.delete(`/transactions/${id}`),
    async bulkDelete(ids) {
      const response = await api.post("/transactions/bulk-delete", { ids });
      return response.data;
    },
    async importCsv(file, accountId) {
      const form = new FormData();
      form.append("file", file);
      if (accountId) form.append("account_id", accountId);
      const response = await api.post("/transactions/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    async saveTransaction(editing, payload) {
      const response = editing
        ? await api.put(`/transactions/${editing.id}`, payload)
        : await api.post("/transactions", payload);
      return response.data;
    },
    async saveTransfer(transferId, payload) {
      const response = transferId
        ? await api.put(`/transfers/${transferId}`, payload)
        : await api.post("/transfers", payload);
      return response.data;
    },
    async linkTransfer(transactionId, otherTransactionId) {
      const response = await api.post("/transfers/link", {
        transaction_id: transactionId, other_transaction_id: otherTransactionId,
      });
      return response.data;
    },
    async unlinkTransfer(transferId) {
      const response = await api.post(`/transfers/${transferId}/unlink`);
      return response.data;
    },
    async transferCandidates(transactionId) {
      const response = await api.get(`/transactions/${transactionId}/transfer-candidates`);
      return response.data;
    },
    async previewGooglePay(file) {
      const form = new FormData();
      form.append("file", file);
      const response = await api.post("/transactions/gpay/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    async commitGooglePay(rows, accountId) {
      const response = await api.post("/transactions/gpay/commit", {
        rows, account_id: accountId,
      });
      return response.data;
    },
  };
}

export function useLedgerInteraction(backend, scope = ALL_ACCOUNTS) {
  const [ledger] = useState(() => new LedgerInteraction(backend, { scope }));
  const state = useSyncExternalStore(ledger.subscribe, ledger.getSnapshot, ledger.getSnapshot);
  useEffect(() => {
    ledger.start().catch(() => {});
    return () => ledger.destroy();
  }, [ledger]);
  useEffect(() => {
    ledger.setScope(scope).catch(() => {});
  }, [ledger, scope]);
  return { state, ledger };
}
